import { StrictMode, useEffect } from 'react'

import Clarity from '@microsoft/clarity'
import * as Sentry from '@sentry/react'
import { createRoot } from 'react-dom/client'
import './globals.css'

import {
  BrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'

import ApiErrorRegistrar from './ApiErrorRegistrar'
import { Toaster } from './components/ui/sonner'
import AuthProvider from './contexts/Auth0Provider'
import { ErrorBoundary, RootErrorBoundary } from './ErrorBoundary'
import { showModal } from './lib/utils/showModal'
import { normalizeTransactionName } from './lib/sentry'
import ProtectedApp from './ProtectedApp.jsx'
import {
  registerViewportRestoreListeners,
  restoreFullHeight,
} from './utils/viewport'
import { TooltipProvider } from './components/ui/tooltip'

const isDevelopment = import.meta.env.VITE_SENTRY_ENVIRONMENT === 'development'
console.log({isDevelopment})

Sentry.init({
  dsn: 'https://4cb3f2bafaddc18458aeb9f9d5dea8d3@o1255569.ingest.us.sentry.io/4510505279225856',
  enabled: !isDevelopment, // Enable in production builds
  environment: isDevelopment ? 'development' : 'production',
  telemetry: false,
  sendDefaultPii: true,
  enableLogs: true,

  integrations: [
    // ✅ Only React Router integration (includes browser tracing)
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),

    // ✅ Control breadcrumbs
    Sentry.breadcrumbsIntegration({
      console: !isDevelopment, // Only console breadcrumbs in production
      dom: true,
      fetch: true,
      history: true,
      xhr: true,
    }),
    Sentry.consoleLoggingIntegration({ levels: ["log", "warn", "error"] }),
  ],

  tracesSampleRate: !isDevelopment ? 0.1 : 1.0,

  tracePropagationTargets: [
    'localhost',
    'https://pilot.edelman-ai.app',
  ],

  beforeSendTransaction(transaction) {
    // Normalize chat routes
    transaction.name = normalizeTransactionName(transaction.name)
    return transaction
  },

  beforeSend(event) {
    // Don't send events in dev
    if (isDevelopment) {
      console.error('Sentry event (dev - not sent):', event)
      return null
    }
    return event
  },
})
/**
 * Checks if the app is running in a PWA environment.
 * (added to home screen on mobile/desktop)
 *
 * @returns {boolean}  True if the app is running in a PWA environment
 */
export function isPWA() {
  const isMobile =
    /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  if (!isMobile) {
    return false
  }
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer?.startsWith('android-app://')
  )
}
window.handle401 = () => {
  console.log('handle401')
  window.localStorage.clear()
  window.location.reload()
}

window.handle500 = () => {
  showModal()
}
const { location } = window
const params = Object.fromEntries(new URLSearchParams(location.search))
if (params.error) {
  console.error('[main] error →', params.error)
  throw new Error(params.error)
}
if (isPWA()) {
  restoreFullHeight()
  registerViewportRestoreListeners()
}

if (!isDevelopment){
  Clarity.init('uiu50oqui6')
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <RootErrorBoundary>
        <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <ErrorBoundary name="ProtectedApp">
              <ProtectedApp />
            </ErrorBoundary>
          </AuthProvider>
          <Toaster position="top-right" defaultDuration={8000} />
          <ApiErrorRegistrar />
        </BrowserRouter>
        </TooltipProvider>
    </RootErrorBoundary>
  </StrictMode>,
)